import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Bell, Plane } from "lucide-react";
import { API, SLOT_LABEL, auth } from "./practiceApi";

const fmt = (iso) => new Date(`${iso}T12:00:00`).toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" });
const pill = "rounded-full border border-[#d7bb7a]/30 px-3 py-1.5 text-xs text-[#f3ecd9] transition-colors hover:bg-white/5 disabled:opacity-50";

// Holiday pause: nudges rest for a few days and come back on their own; the 21 days carry on.
export const HolidayPause = ({ p, onChange }) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const set = async (days) => {
    setBusy(true);
    try {
      const { data } = await axios.patch(`${API}/api/practice`, { reminders_off_days: days }, auth());
      onChange(data);
      setOpen(false);
      toast.success(days ? `Reminders resting until ${fmt(data.reminders_off_until)}.` : "Reminders are back on.");
    } catch { toast.error("Something went wrong. Please try again."); }
    setBusy(false);
  };
  if (p.reminders_off_until) {
    return (
      <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="practice-holiday-active">
        <p className="text-xs text-[#9fb39a]" data-testid="practice-holiday-until"><Plane size={12} className="mr-1 inline" />Reminders resting until {fmt(p.reminders_off_until)}. They'll come back on their own.</p>
        <button type="button" className={pill} disabled={busy} onClick={() => set(0)} data-testid="practice-holiday-resume">Resume now</button>
      </div>
    );
  }
  return (
    <div className="mt-3" data-testid="practice-holiday">
      {open ? (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs text-[#cfc6ae]">Rest reminders for</span>
          {[3, 7, 14].map((d) => <button key={d} type="button" className={pill} disabled={busy} onClick={() => set(d)} data-testid={`practice-holiday-${d}`}>{d} days</button>)}
          <button type="button" className="px-2 text-xs text-[#9a937f]" onClick={() => setOpen(false)} data-testid="practice-holiday-cancel">Cancel</button>
        </div>
      ) : (
        <button type="button" className="inline-flex items-center gap-1.5 text-xs text-[#cfc6ae] underline-offset-4 hover:underline" onClick={() => setOpen(true)} data-testid="practice-holiday-open"><Plane size={12} /> Going away? Pause reminders</button>
      )}
    </div>
  );
};

// Exactly what each reminder will say, and when.
export const ReminderPreview = ({ p, title = "Soulumo" }) => {
  const [open, setOpen] = useState(false);
  const slots = [...new Set([...(p.slots || []), ...(p.night ? ["night"] : [])])].filter((s) => p.reminder_notes?.[s]);
  return (
    <div className="mt-3" data-testid="practice-reminder-preview">
      <button type="button" className="inline-flex items-center gap-1.5 text-xs text-[#cfc6ae] underline-offset-4 hover:underline" onClick={() => setOpen(!open)} data-testid="practice-preview-toggle">
        <Bell size={12} /> {open ? "Hide preview" : "Preview my reminders"}
      </button>
      {open && (
        <div className="mt-2 space-y-2">
          {slots.map((s) => (
            <div key={s} className="flex items-start gap-3 rounded-2xl bg-white/[0.06] p-3" data-testid={`practice-preview-${s}`}>
              <img src="/brand-icons/soulumo-192.png" alt="" className="h-8 w-8 rounded-lg" />
              <div className="min-w-0 flex-1">
                <p className="flex justify-between text-xs text-[#f3ecd9]"><span className="font-semibold">{title}</span><span className="text-[#9a937f]">{SLOT_LABEL[s] || s} · {p.times?.[s]}</span></p>
                <p className="mt-0.5 text-sm text-[#cfc6ae]">{p.reminder_notes[s].replace("Soulumo", title)}</p>
              </div>
            </div>
          ))}
          <p className="text-[0.7rem] text-[#9a937f]">Each one only arrives if you haven't already done that part of the day.</p>
        </div>
      )}
    </div>
  );
};
